'use client';

import React, {
  type ForwardRefRenderFunction,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Avatar, Button, Drawer, Form, Input, Modal, notification } from 'antd';
import { useConnection, useUser } from '@sezenta/connection';
import { useBusinessContext } from '../../context/BusinessContext';
import { Permission } from '../../dto';

export type UserViewModalInstance = {
  view: (user: any) => void;
};
type Props = { onEdit?: (user: any) => void };
const UserViewModal: ForwardRefRenderFunction<UserViewModalInstance, Props> = (
  props,
  ref,
) => {
  const [user, setUser] = useState<any>();
  const [api, contextHolder] = notification.useNotification();
  const connection = useConnection();
  const authUser = useUser();
  const { businessId } = useBusinessContext();

  // State for password reset modal
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useImperativeHandle(
    ref,
    () => ({
      view: (u: any) => {
        setUser(u);
      },
    }),
    [],
  );

  const resetPassword = () => {
    if (!password) {
      api.error({
        message: 'Password Reset',
        description: 'Password is required',
      });
      return;
    }

    setLoading(true);
    connection
      .post(`/auth/${businessId}/admin/change-password`, {
        password,
        userId: user.userId,
      })
      .then((res) => {
        if (res.status === 201) {
          api.success({
            message: 'Password Reset',
            description: 'Password reset successfully',
          });
          setPassword('');
          setIsPasswordModalOpen(false); // Close the modal on success
        }
      })
      .catch(() => {
        api.error({
          message: 'Password Reset',
          description: 'Failed to reset password',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleOpenPasswordModal = () => {
    setPassword(''); // Clear password field when opening modal
    setIsPasswordModalOpen(true);
  };

  const handleClosePasswordModal = () => {
    setIsPasswordModalOpen(false);
  };

  return (
    <>
      <Drawer
        width={500}
        destroyOnClose={true}
        title={'View User'}
        open={!!user}
        onClose={() => {
          setUser(undefined);
          setIsPasswordModalOpen(false);
        }}
        footer={
          <div className="flex justify-end">
            {authUser?.permissions.includes(Permission.EDIT_USER) && (
              <Button type="primary" onClick={handleOpenPasswordModal}>
                Reset Password
              </Button>
            )}
          </div>
        }
      >
        {user && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[300px] border-spacing-y-4 border-separate mt-[-15px]">
              <tbody>
                <tr>
                  <td className="flex items-start font-semibold pr-4">
                    Profile Picture
                  </td>
                  <td>
                    <Avatar
                      src={user.picture}
                      alt="profile"
                      className="w-16 h-16 rounded-[10px]"
                      size={200}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="flex items-start font-semibold pr-4">
                    First Name
                  </td>
                  <td className="text-gray-800">{user.firstName}</td>
                </tr>
                <tr>
                  <td className="flex items-start font-semibold pr-4">
                    Last Name
                  </td>
                  <td className="text-gray-800">{user.lastName}</td>
                </tr>
                <tr>
                  <td className="flex items-start font-semibold pr-4">Email</td>
                  <td className="text-gray-800">{user.email}</td>
                </tr>
                <tr>
                  <td className="flex items-start font-semibold pr-4">
                    Login ID
                  </td>
                  <td className="text-gray-800">{user.userName}</td>
                </tr>
                {/* <tr>
                  <td className="flex items-start font-semibold">Role</td>
                  <td className="text-gray-800">{user.role}</td>
                </tr> */}
              </tbody>
            </table>
          </div>
        )}
        {contextHolder}
      </Drawer>

      {/* Password Reset Modal */}
      <Modal
        title="Reset Password"
        open={isPasswordModalOpen}
        onCancel={handleClosePasswordModal}
        zIndex={1050} // Ensure the modal appears above the drawer
        footer={[
          <Button key="cancel" onClick={handleClosePasswordModal}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            disabled={!password}
            onClick={resetPassword}
          >
            Reset Password
          </Button>,
        ]}
      >
        <Form layout="vertical" className="mt-4">
          <Form.Item
            label="New Password"
            required
            rules={[{ required: true, message: 'Please enter a new password' }]}
          >
            <Input.Password
              placeholder="Enter new password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export const useUserViewModal = () => useRef<UserViewModalInstance>(null);

export default React.forwardRef<UserViewModalInstance, Props>(UserViewModal);
